import * as React from "react";
import { Dialog as AtlasDialog } from "@wellsky/atlas-ui-react";
import { PortalContext } from "../../widget/WidgetProvider";

import { cn } from "./utils";

interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}

function Dialog({ open, onOpenChange, children }: DialogProps) {
  return (
    <DialogContext.Provider value={{ open, onOpenChange }}>
      {open ? children : null}
    </DialogContext.Provider>
  );
}

const DialogContext = React.createContext<{
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}>({});

function DialogTrigger({ children, ...props }: React.ComponentProps<"button">) {
  const { onOpenChange } = React.useContext(DialogContext);
  return (
    <button data-slot="dialog-trigger" onClick={() => onOpenChange?.(true)} {...props}>
      {children}
    </button>
  );
}

function DialogContent({
  className,
  children,
  style,
  ...props
}: React.ComponentProps<"div"> & { style?: React.CSSProperties }) {
  const portalContainer = React.useContext(PortalContext);
  const { open, onOpenChange } = React.useContext(DialogContext);

  if (!open) return null;

  return (
    <AtlasDialog
      data-slot="dialog-content"
      open={true}
      onClose={() => onOpenChange?.(false)}
      container={portalContainer as HTMLElement | undefined}
      className={cn(
        "bg-background w-full max-w-[calc(100%-2rem)] gap-4 rounded-lg border p-6 shadow-lg sm:max-w-lg",
        className,
      )}
      style={style}
      {...props}
    >
      {children}
    </AtlasDialog>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<"h2">) {
  return (
    <h2
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function DialogClose({ children, ...props }: React.ComponentProps<"button">) {
  const { onOpenChange } = React.useContext(DialogContext);
  return (
    <button data-slot="dialog-close" onClick={() => onOpenChange?.(false)} {...props}>
      {children}
    </button>
  );
}

function DialogOverlay(_props: React.ComponentProps<"div">) {
  return null; // atlas-ui Dialog handles its own overlay
}

function DialogPortal({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
